import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { List, MapIcon, MapPin, Search, X } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { getStoredLocation, requestLocation, type Coords } from "@/lib/location";
import { CATEGORIES, AGE_GROUPS, distanceKm } from "@/lib/constants";
import { translateOption, useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth-context";
import { Navbar } from "@/components/Navbar";
import { BookCardItem, type BookCard } from "@/components/BookCard";
import { LazyBookMap } from "@/components/LazyBookMap";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { pageHead, jsonLd, SITE_URL } from "@/lib/seo";

const VIEW_KEY = "bookswap_view_mode";

export const Route = createFileRoute("/browse")({
  head: () => ({
    ...pageHead({
      path: "/browse",
      title: "Browse books near you — Book Swap",
      description: "Find books to borrow, swap or receive near you on Book Swap. Filter by distance, category and exchange type — free and local.",
    }),
    scripts: [
      jsonLd({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        name: "Browse books near you",
        description: "Books shared by neighbours on Book Swap, filterable by distance, category and exchange type.",
        url: `${SITE_URL}/browse`,
        isPartOf: { "@type": "WebSite", name: "Book Swap", url: SITE_URL },
        about: { "@type": "Thing", name: "Local book borrowing, swapping and donation" },
      }),
    ],
  }),

  component: Browse,
});

function Browse() {
  const { lang, t } = useI18n();
  const { user } = useAuth();
  const [coords, setCoords] = useState<Coords | null>(null);
  const [locError, setLocError] = useState<string | null>(null);
  const [books, setBooks] = useState<BookCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState<string>("all");
  const [age, setAge] = useState<string>("all");
  const [nearbyOnly, setNearbyOnly] = useState(false);
  const [donationsOnly, setDonationsOnly] = useState(false);
  const [view, setView] = useState<"list" | "map">("list");
  const [radiusKm, setRadiusKm] = useState<number>(25);

  useEffect(() => {
    const stored = getStoredLocation();
    if (stored) setCoords(stored);
    if (typeof window !== "undefined") {
      const v = localStorage.getItem(VIEW_KEY);
      if (v === "map" || v === "list") setView(v);
    }
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined") localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    supabase.from("books").select("id,title,author,cover_url,category,age_group,exchange_duration,condition,lat,lng,status,is_donation,user_id")
      .order("created_at", { ascending: false })
      .limit(200)
      .then(({ data, error }) => {
        if (cancelled) return;
        setLoading(false);
        if (error) { console.error(error); return toast.error(lang === "ar" ? "تعذّر تحميل الكتب." : "Could not load books."); }
        const rows = data ?? [];
        rows.sort((a: any, b: any) => Number(a.status !== "available") - Number(b.status !== "available"));
        setBooks(rows);
      });
    const channel = supabase
      .channel("books-browse")
      .on("postgres_changes", { event: "*", schema: "public", table: "books" }, (payload) => {
        const newRow: any = payload.new;
        const oldRow: any = payload.old;
        if (payload.eventType === "DELETE") {
          setBooks((prev) => prev.filter((b) => b.id !== oldRow?.id));
          return;
        }
        if (!newRow) return;
        setBooks((prev) => {
          const exists = prev.some((b) => b.id === newRow.id);
          const next = exists
            ? prev.map((b) => (b.id === newRow.id ? { ...b, ...newRow } : b))
            : [newRow as BookCard, ...prev];
          return [...next].sort((a: any, b: any) => Number(a.status !== "available") - Number(b.status !== "available"));
        });
      })
      .subscribe();
    return () => {
      cancelled = true;
      supabase.removeChannel(channel);
    };
  }, []);

  const filtered = useMemo(() => {
    return books.filter((b) => {
      if (donationsOnly && !b.is_donation) return false;
      if (nearbyOnly && coords && b.lat != null && b.lng != null && distanceKm(coords.lat, coords.lng, b.lat, b.lng) > radiusKm) return false;
      if (nearbyOnly && coords && (b.lat == null || b.lng == null)) return false;
      if (cat !== "all" && b.category !== cat) return false;
      if (age !== "all" && b.age_group !== age) return false;
      if (q && !`${b.title} ${b.author}`.toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [books, coords, cat, age, q, nearbyOnly, donationsOnly, radiusKm]);

  const withinRadius = useMemo(() => {
    if (!coords) return 0;
    return filtered.filter(
      (b) => b.lat != null && b.lng != null && distanceKm(coords.lat, coords.lng, b.lat, b.lng) <= radiusKm,
    ).length;
  }, [filtered, coords, radiusKm]);

  const unmappedCount = useMemo(
    () => filtered.filter((b) => b.lat == null || b.lng == null).length,
    [filtered],
  );

  async function askLocation() {
    setLocError(null);
    try { setCoords(await requestLocation()); setNearbyOnly(true); }
    catch (e: any) { setLocError(lang === "ar" ? "تعذّر تحديد موقعك" : (e.message ?? "Could not get your location")); }
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="mx-auto max-w-6xl px-4 py-8">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold">{t("browse.title")}</h1>
            <p className="text-sm text-muted-foreground">
              {coords && nearbyOnly ? t("browse.subtitle_nearby", { km: radiusKm }) : t("browse.subtitle_all")}
            </p>
          </div>
          {coords ? (
            <Button
              variant={nearbyOnly ? "default" : "outline"}
              size="sm"
              onClick={() => setNearbyOnly((v) => !v)}
              className={nearbyOnly ? "bg-primary hover:bg-primary/90" : ""}
            >
              <MapPin className="mr-1 h-4 w-4" />
              {nearbyOnly ? t("browse.showing_nearby") : t("browse.show_nearby")}
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={askLocation}>
              <MapPin className="mr-1 h-4 w-4" /> {t("browse.use_location")}
            </Button>
          )}
        </div>
        {locError && <p className="mt-2 text-sm text-destructive">{locError}</p>}

        <div className="mt-4 inline-flex rounded-full border bg-card p-1">
          <Button
            size="sm"
            variant={view === "list" ? "default" : "ghost"}
            className="rounded-full"
            onClick={() => setView("list")}
          >
            <List className="mr-1 h-4 w-4" /> {t("browse.list_view")}
          </Button>
          <Button
            size="sm"
            variant={view === "map" ? "default" : "ghost"}
            className="rounded-full"
            onClick={() => setView("map")}
          >
            <MapIcon className="mr-1 h-4 w-4" /> {t("browse.map_view")}
          </Button>
        </div>

        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Button
            size="sm"
            variant={donationsOnly ? "default" : "outline"}
            onClick={() => setDonationsOnly((v) => !v)}
            className={donationsOnly ? "bg-success hover:bg-success/90" : ""}
          >
            💝 {donationsOnly ? (lang === "ar" ? "عرض التبرعات فقط" : "Showing donations only") : (lang === "ar" ? "التبرعات فقط" : "Donations only")}
          </Button>
          {coords && (
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground">{lang === "ar" ? "نطاق البحث:" : "Search radius:"}</span>
              <Select value={String(radiusKm)} onValueChange={(v) => { setRadiusKm(Number(v)); setNearbyOnly(true); }}>
                <SelectTrigger className="h-9 w-32 bg-input"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[1, 3, 5, 10, 25, 50, 100].map((r) => (
                    <SelectItem key={r} value={String(r)}>{r} {lang === "ar" ? "كم" : "km"}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div className="mt-4 rounded-2xl bg-gradient-to-r from-primary to-primary/80 px-4 py-3 text-white shadow-[var(--shadow-card)]">
          <p className="text-sm font-medium">
            {!coords
              ? `📚 ${t("browse.share_loc")}`
              : withinRadius > 0
                ? (lang === "ar"
                    ? `📚 ${withinRadius} كتاب متاح ضمن ${radiusKm} كم منك`
                    : `📚 ${withinRadius} ${withinRadius === 1 ? "book" : "books"} available within ${radiusKm} km of you`)
                : filtered.length > 0
                  ? (lang === "ar"
                      ? `📖 ${filtered.length} كتاب متاح — ${unmappedCount > 0 ? "بعضها بدون موقع على الخريطة بعد" : "وسّع نطاق البحث لرؤيتها"}`
                      : `📖 ${filtered.length} ${filtered.length === 1 ? "book" : "books"} available — ${unmappedCount > 0 ? `${unmappedCount} not pinned on the map yet` : "try a wider search radius"}`)
                  : (lang === "ar"
                      ? `📚 لا توجد كتب ضمن ${radiusKm} كم منك بعد`
                      : `📚 No books within ${radiusKm} km of you yet — try a wider radius`)}
          </p>
        </div>

        <div className="mt-6 flex flex-col gap-3 rounded-2xl bg-primary-soft p-4 md:flex-row">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} aria-label={lang === "ar" ? "ابحث عن الكتب" : "Search books"} placeholder={t("browse.search_ph")} className="bg-input pl-9" />
          </div>
          <Select value={cat} onValueChange={setCat}>
            <SelectTrigger className="bg-input md:w-52"><SelectValue placeholder={lang === "ar" ? "التصنيف" : "Category"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("browse.cat_all")}</SelectItem>
              {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{translateOption(c, lang)}</SelectItem>)}
            </SelectContent>
          </Select>
          <Select value={age} onValueChange={setAge}>
            <SelectTrigger className="bg-input md:w-52"><SelectValue placeholder={lang === "ar" ? "الفئة العمرية" : "Age group"} /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{t("browse.age_all")}</SelectItem>
              {AGE_GROUPS.map((c) => <SelectItem key={c} value={c}>{translateOption(c, lang)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>

        {(() => {
          const activeCount = (q ? 1 : 0) + (cat !== "all" ? 1 : 0) + (age !== "all" ? 1 : 0) + (donationsOnly ? 1 : 0) + (nearbyOnly ? 1 : 0);
          if (activeCount === 0) return null;
          return (
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className="rounded-full bg-primary-soft px-3 py-1 text-xs font-medium text-primary">
                {activeCount === 1 ? t("browse.filters_active", { n: activeCount }) : t("browse.filters_active_plural", { n: activeCount })}
              </span>
              <Button
                size="sm"
                variant="ghost"
                className="h-7 rounded-full px-3 text-xs"
                onClick={() => { setQ(""); setCat("all"); setAge("all"); setDonationsOnly(false); setNearbyOnly(false); }}
              >
                <X className="mr-1 h-3 w-3" /> {t("browse.clear_filters")}
              </Button>
            </div>
          );
        })()}

        {!user && (
          <div className="mt-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-card px-4 py-3 shadow-sm">
            <p className="text-sm text-muted-foreground">{t("browse.guest_cta")}</p>
            <Link to="/register">
              <Button size="sm" className="bg-primary hover:bg-primary/90">{t("browse.guest_cta_btn")}</Button>
            </Link>
          </div>
        )}

        <h2 className="sr-only">{lang === "ar" ? "الكتب المتاحة" : "Available books"}</h2>

        {loading ? (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-2xl bg-card p-3 shadow-sm">
                <div className="aspect-[3/4] w-full rounded-xl bg-muted" />
                <div className="mt-3 h-4 w-3/4 rounded bg-muted" />
                <div className="mt-2 h-3 w-1/2 rounded bg-muted" />
              </div>
            ))}
          </div>
        ) : view === "map" ? (
          <div className="mt-6 space-y-3">
            {(() => {
              const myUnpinned = user ? books.filter((b) => b.user_id === user.id && (b.lat == null || b.lng == null)) : [];
              if (myUnpinned.length === 0) return null;
              const placeMine = async () => {
                try {
                  const c = coords ?? (await requestLocation());
                  setCoords(c);
                  const ids = myUnpinned.map((b) => b.id);
                  const { error } = await supabase.from("books").update({ lat: c.lat, lng: c.lng }).in("id", ids);
                  if (error) throw error;
                  setBooks((prev) => prev.map((b) => (ids.includes(b.id) ? { ...b, lat: c.lat, lng: c.lng } : b)));
                  toast.success(lang === "ar" ? "تم تثبيت كتبك على الخريطة" : "Your books are now on the map");
                } catch (e: any) {
                  toast.error(lang === "ar" ? "تعذّر الحصول على موقعك" : (e?.message ?? "Could not get your location"));
                }
              };
              return (
                <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-primary/20 bg-primary-soft px-4 py-3">
                  <p className="text-sm text-foreground">
                    📍 {lang === "ar"
                      ? `${myUnpinned.length} من كتبك بدون موقع على الخريطة`
                      : `${myUnpinned.length} of your ${myUnpinned.length === 1 ? "book is" : "books are"} missing a map location`}
                  </p>
                  <Button size="sm" onClick={placeMine} className="bg-primary hover:bg-primary/90">
                    {lang === "ar" ? "ضع كتبي على الخريطة" : "Place my books on the map"}
                  </Button>
                </div>
              );
            })()}
            <LazyBookMap books={filtered} here={coords} />
            {filtered.length === 0 && (
              <p className="mt-4 text-center text-sm text-muted-foreground">{lang === "ar" ? "لا توجد كتب تطابق الفلاتر." : "No books match your filters."}</p>
            )}
          </div>
        ) : filtered.length === 0 ? (
          books.length === 0 ? (
            <div className="mt-12 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
              <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-primary-soft text-3xl" aria-hidden="true">📚</div>
              <h2 className="mt-4 text-xl font-semibold">{t("browse.empty_first_title")}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t("browse.empty_first_sub")}</p>
              <Link to="/add-book" className="mt-6 inline-block">
                <Button className="bg-primary hover:bg-primary/90">{t("browse.empty_first_cta")}</Button>
              </Link>
            </div>
          ) : (
            <div className="mt-16 rounded-2xl bg-card p-10 text-center shadow-[var(--shadow-card)]">
              <h2 className="text-lg font-semibold">{t("browse.empty_title")}</h2>
              <p className="mt-2 text-sm text-muted-foreground">{t("browse.empty_sub")}</p>
            </div>
          )
        ) : (
          <div className="mt-6 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {filtered.map((b) => <BookCardItem key={b.id} book={b} here={coords} />)}
          </div>
        )}
      </div>
    </div>
  );
}
